import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'About Us', href: '#about', page: '/' },
  { label: 'I am a Seller', href: '/sellers', page: '/sellers' },
  { label: 'I am a Real Estate Agent', href: '/agents', page: '/agents' },
  { label: 'FAQ', href: '#faq', page: '/' },
  { label: 'Contact', href: '#contact', page: '/' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link: typeof navLinks[0]) => {
    setMenuOpen(false);

    // If it's a page route (not a hash)
    if (!link.href.startsWith('#')) {
      navigate(link.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // It's a hash link — navigate to home first if needed
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogoClick = () => {
    setMenuOpen(false);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGetOffer = () => {
    setMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-white border-b border-gray-100'
      }`}
    >
      <div className={`max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-24 md:h-28' : 'h-16 md:h-20'}`}>
        {/* Logo — hidden until scrolled */}
        <button
          onClick={handleLogoClick}
          className={`flex items-center cursor-pointer transition-all duration-300 ${
            scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
          }`}
        >
          <img
            src="https://static.readdy.ai/image/97714bd9b358da41691b55298de3f442/a3ed0c045a06ae061d06a7b185ca2783.png"
            alt="ReadyBuyer Group"
            className="h-20 md:h-24 w-auto object-contain"
          />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === link.page && !link.href.startsWith('#')
                  ? 'text-teal-700 bg-teal-50'
                  : 'text-gray-700 hover:text-teal-700 hover:bg-teal-50'
              }`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={handleGetOffer}
            className="ml-3 px-5 py-2 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Get an Offer
          </button>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer text-gray-700"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="flex flex-col px-4 py-3 gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className="px-3 py-3 text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 rounded-md cursor-pointer text-left whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={handleGetOffer}
              className="mt-2 px-5 py-3 bg-teal-700 text-white text-sm font-semibold rounded-full text-center hover:bg-teal-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get an Offer
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
